import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, BellRing, X } from 'lucide-react';
import api from '../api/axios';

const timeAgo = (date) => {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return "À l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
};

export default function NotificationBell({ salonId }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/admin/notifications');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch {
      // silently fail - non-critical
    }
  }, []);

  // Initial fetch + polling every 30s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleMarkAllRead = async () => {
    setMarking(true);
    try {
      await api.put('/admin/notifications/read');
      await fetchNotifications();
      setOpen(false);
    } catch {
      // silently fail
    } finally {
      setMarking(false);
    }
  };

  const hasUnread = unreadCount > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-all"
        aria-label="Notifications"
      >
        {hasUnread ? (
          <BellRing size={22} className="text-blue-600" />
        ) : (
          <Bell size={22} />
        )}
        {hasUnread && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="font-black text-slate-800 text-sm">Notifications</p>
            <button
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
            {notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={28} className="text-slate-200 mx-auto mb-2" />
                <p className="text-slate-400 text-sm font-bold">Aucune notification</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((notif) => (
                <div
                  key={notif._id}
                  className={`px-4 py-3 transition-colors ${
                    !notif.isRead ? 'bg-blue-50/60' : 'hover:bg-slate-50'
                  }`}
                >
                  <p className="text-sm font-semibold text-slate-800 leading-snug break-words text-left">{notif.message}</p>
                  <p className="text-[11px] text-slate-400 font-medium mt-1 text-left">{timeAgo(notif.createdAt)}</p>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100">
              <button
                onClick={handleMarkAllRead}
                disabled={marking || unreadCount === 0}
                className="w-full py-2 text-xs font-black text-blue-600 hover:bg-blue-50 rounded-xl transition-all disabled:opacity-40"
              >
                {marking ? 'Chargement...' : 'Tout marquer comme lu'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
